// ui/src/components/shared/DataFreshnessIndicator.jsx

/**
 * @file DataFreshnessIndicator.jsx
 * @description Inline freshness signal for quota, credits, population,
 *   projection, and calculation data. Renders a coloured dot, signal label,
 *   status label, optional count, exception count, and relative timestamp.
 *   Formatters imported from ProvisionalBadge. No API calls — prop-driven.
 * @module shared
 * @author data-fit LLC · DecisionFlow · S775
 * @see api_spec_fileA_v4_final.md §1.4 — quotaFreshness / creditFreshness shapes
 */

import React from 'react';
import {
  relativeTime,
  fullDateTime,
} from './ProvisionalBadge';

const SIGNAL_LABELS = {
  quota: 'Quota',
  credits: 'Credits',
  population: 'Positions',
  projection: 'Projection',
  calculation: 'Last calculation',
};

const STATUS_LABELS = {
  current: 'current',
  warning: 'warning',
  stale: 'stale',
  unknown: 'unknown',
};

const STATUS_COLORS = {
  current: 'rgb(var(--color-success))',
  warning: 'rgb(var(--color-warning))',
  stale: 'rgb(var(--color-danger))',
  unknown: 'rgb(var(--color-text-muted))',
};

/**
 * @param {{
 *   signal: 'quota'|'credits'|'population'|'projection'|'calculation',
 *   freshnessAt: string|null,
 *   status: 'current'|'stale'|'warning'|'unknown',
 *   count?: number,
 *   exceptionCount?: number,
 *   className?: string
 * }} props
 */
export default function DataFreshnessIndicator({
  signal,
  freshnessAt,
  status,
  count,
  exceptionCount = 0,
  className = '',
}) {
  const signalLabel = SIGNAL_LABELS[signal] || signal;
  const statusLabel = STATUS_LABELS[status] || 'unknown';
  const statusColor =
    STATUS_COLORS[status] || STATUS_COLORS.unknown;

  const freshness = relativeTime(freshnessAt);
  const fullTimestamp = fullDateTime(freshnessAt);

  const hasCount = typeof count === 'number';
  const hasExceptions =
    typeof exceptionCount === 'number' && exceptionCount > 0;

  const ariaParts = [
    `${signalLabel}: ${statusLabel}`,
    freshnessAt
      ? `last updated ${fullTimestamp}`
      : 'freshness unknown',
    hasCount ? `${count} reported` : null,
    hasExceptions ? `${exceptionCount} exceptions` : null,
  ].filter(Boolean);

  return (
    <span
      className={`inline-flex flex-wrap items-center gap-1.5 text-[11px] leading-4 ${className}`}
      aria-label={ariaParts.join(', ')}
      title={ariaParts.join(', ')}
    >
      <span
        className="inline-block h-2 w-2 shrink-0 rounded-[999px]"
        style={{ backgroundColor: statusColor }}
        aria-hidden="true"
      />

      <span
        className="font-medium"
        style={{
          color:
            status === 'stale'
              ? 'rgb(var(--color-danger))'
              : 'rgb(var(--color-text))',
        }}
      >
        {signalLabel}
      </span>

      <span
        className="font-medium"
        style={{ color: statusColor }}
      >
        {statusLabel}
      </span>

      {hasCount && (
        <>
          <span
            className="text-[rgb(var(--color-text-muted))]"
            aria-hidden="true"
          >
            ·
          </span>

          <span className="text-[rgb(var(--color-text-muted))]">
            {count}
          </span>
        </>
      )}

      {hasExceptions && (
        <>
          <span
            className="text-[rgb(var(--color-text-muted))]"
            aria-hidden="true"
          >
            ·
          </span>

          <span className="font-medium text-[rgb(var(--color-danger))]">
            {exceptionCount} exceptions
          </span>
        </>
      )}

      <span
        className="text-[rgb(var(--color-text-muted))]"
        aria-hidden="true"
      >
        ·
      </span>

      <span className="text-[rgb(var(--color-text-muted))]">
        {freshness}
      </span>
    </span>
  );
}
